import { module } from 'modujs';
import { CLASS as FORM_CLASS } from './Form';
import { getValidity, reportValidity } from './FormValidation';
import { whenApiIsReady } from '../utils/api-hitit.js';
import { html } from '../utils/environment';

/**
 * @typedef {{ [Port.countryName]: Port[] }} PortGroups
 */

/**
 * @typedef {object} Port
 *
 * @property {string} city        - Code for city; Mostly the same value as `code`.
 * @property {string} cityName    - Name of city.
 * @property {string} code        - IATA code for airport.
 * @property {string} country     - ISO 3166-1 alpha-2 code for country.
 * @property {string} countryName - Name of country.
 * @property {string} portName    - Name of airport.
 * @property {string} timeZone    - Timezone of airport.
 */

/**
 * @typedef {object} Currencies
 *
 * @property {Currency}   defaultCurrency - Default currency.
 * @property {Currency[]} values          - List of accepted currencies.
 */

/**
 * @typedef {object} Currency
 *
 * @property {string} code         - ISO 4217 alpha-3 code for currency.
 * @property {number} doubleDigits - The number of digits after the decimal separator.
 */

/**
 * @typedef {Function} OptionMapper
 *
 * @param  {HTMLOptionElement} option
 * @param  {any}               item
 * @param  {number}            index
 * @param  {any[]}             items
 * @return {HTMLOptionElement}
 */

const DEFAULT_CURRENCY = 'CAD';
const DEFAULT_MAX_PASSENGER_COUNT = 10;
const DEFAULT_MAX_SEGMENT_COUNT   = 5;

const TRIP_TYPE_MULTITRIP  = 'MULTI_DIRECTIONAL';
const TRIP_TYPE_ONEWAYTRIP = 'ONE_WAY';
const TRIP_TYPE_ROUNDTRIP  = 'ROUND_TRIP';

const FORM_CONTROL_TRIP_TYPE    = 'tripType';
const FORM_CONTROL_CABIN_CLASS  = 'cabinClass';
const FORM_CONTROL_CURRENCY     = 'currency';
const FORM_CONTROL_COUPON_PROMO = 'promoCode';
const FORM_CONTROL_COUPON_RAAP  = 'accountCode';

const FORM_CONTROL_DEPARTURE_DATE = 'departureDate';
const FORM_CONTROL_RETURN_DATE    = 'returnDate';

const FORM_CONTROL_DEPARTURE_LOCATION = 'departureLocation';
const FORM_CONTROL_ARRIVAL_LOCATION   = 'arrivalLocation';

const FORM_CONTROL_PASSENGER_COUNT         = 'passengers';
const FORM_CONTROL_SENIORS_PASSENGER_COUNT = 'seniors';
const FORM_CONTROL_ADULTS_PASSENGER_COUNT  = 'adults';
const FORM_CONTROL_CHILDS_PASSENGER_COUNT  = 'childs';
const FORM_CONTROL_INFANTS_PASSENGER_COUNT = 'infants';

const apiCache = new class {
    /**
     * @type {string[]|null}
     */
    cabinClasses = null;

    /**
     * @type {Currency[]|null}
     */
    currencies = null;

    /**
     * @type {number|null}
     */
    maxPassengerCount = null;

    /**
     * @type {number|null}
     */
    maxSegmentCount = null;

    /**
     * @type {PortGroups|null}
     */
    portGroups = null;

    /**
     * @type {{ [Port.code]: PortGroups[] }}
     */
    portGroupsByPort = {};
}

/**
 * Hitit Flight Booking Form (IBE)
 */
export default class extends module {
    /**
     * Log standard information.
     *
     * @type {boolean}
     */
    DEBUG = false;

    /**
     * Test the initialization state of the API.
     *
     * @type {boolean|number}
     */
    DEBUG_API_INIT = false;

    /**
     * Test the event handling of the module.
     *
     * @type {boolean}
     */
    DEBUG_EVENT = false;

    /**
     * Test form submission.
     *
     * @type {boolean}
     */
    DEBUG_FORM_SUBMIT = false;

    /**
     * Submit form to new window.
     *
     * @type {string|false}
     */
    DEBUG_FORM_TARGET = '_blank';

    /**
     * @type {?CraneSearchAPI}
     */
    api = null;

    /**
     * @type {?HTMLElement}
     */
    #statusElement = null;

    /**
     * @return {boolean}
     */
    get busy() {
        return this.el.getAttribute('aria-busy') === 'true';
    }

    /**
     * @return {?HTMLElement}
     */
    get status() {
        this.#statusElement ??= this.el.querySelector('#booking-form-feedback');
        return this.#statusElement;
    }

    /**
     * @param {boolean} value
     */
    set busy(value) {
        if (value) {
            this.el.setAttribute('aria-busy', value);
        } else {
            this.el.removeAttribute('aria-busy');
        }
    }

    /**
     * @param {boolean} value
     */
    set disabled(value) {
        if (value) {
            this.el.setAttribute('aria-disabled', value);
        } else {
            this.el.removeAttribute('aria-disabled');
        }
    }

    /**
     * @param {string} value
     */
    set status(value) {
        if (this.status) {
            this.status.innerText = value;
        }
    }

    /**
     * @param {object} options
     */
    constructor(options) {
        super(options);

        this.mCaptureEvents = [ 'invalid' ];

        this.events = {
            'addedItem': 'handleAddedItem',
            'change': {
                // 'departure-date': 'handleDepartureDateChange',
                // 'return-date':    'handleReturnDateChange',
                'departure-port': 'handleDeparturePortChange',
                'arrival-port':   'handleArrivalPortChange',
            },
            'click': {
                'swapLocations': 'handleSwapLocations',
            },
            'invalid': 'handleFormInvalid',
            'submit': 'handleFormSubmit',
        };
    }

    /**
     * Handles the custom "addedItem" event from {@see ./DuplicateField.js}.
     *
     * @param {Event} event
     */
    handleAddedItem(event) {
        this.DEBUG_EVENT && console.group('[App.BookingForm]', 'Handle Added Fieldset');
        this.DEBUG_EVENT && console.log('Event:', event);

        if (this.api && apiCache.portGroups) {
            const selects = event.detail.item.querySelectorAll('[data-booking-form="departure-port"]');
            if (selects.length) {
                const options = createSelectOptions(
                    apiCache.portGroups,
                    fillSelectOptionWithPort
                );

                if (options.childElementCount) {
                    selects.forEach((select) => {
                        if (select?.options.length > 1) {
                            return;
                        }

                        select.append(options.cloneNode(true));
                    });
                }
            }
        }

        this.DEBUG_EVENT && console.groupEnd();
    }

    /**
     * Handles the departure date change.
     *
     * @param {Event} event
     *
     * @todo Implement date handling; probably from DatePicker module.
     */
    handleDepartureDateChange(event) {
        this.DEBUG_EVENT && console.group('[App.BookingForm]', 'Handle Change Departure Date');
        this.DEBUG_EVENT && console.log('Event:', event);

        if (this.busy) {
            this.DEBUG_EVENT && console.log('Form is busy');
        } else {
            // TODO
        }

        this.DEBUG_EVENT && console.groupEnd();
    }

    /**
     * Handles the return date change.
     *
     * @param {Event} event
     *
     * @todo Implement date handling; probably from DatePicker module.
     */
    handleReturnDateChange(event) {
        this.DEBUG_EVENT && console.group('[App.BookingForm]', 'Handle Change Return Date');
        this.DEBUG_EVENT && console.log('Event:', event);

        if (this.busy) {
            this.DEBUG_EVENT && console.log('Form is busy');
        } else {
            // TODO
        }

        this.DEBUG_EVENT && console.groupEnd();
    }

    /**
     * Handles the departure/arrival swap values.
     *
     * @param {Event} event
     */
    handleSwapLocations(event) {
        const target = event.currentTarget;
        if (!target.dataset.swapDeparture || !target.dataset.swapArrival) {
            return;
        }

        const departure = document.getElementById(target.dataset.swapDeparture);
        const arrival   = document.getElementById(target.dataset.swapArrival);

        if (!departure || !arrival) {
            return;
        }

        const departureValue = departure.value;
        const arrivalValue   = arrival.value;

        departure.value = arrivalValue;

        // TODO: debug change event not triggering
        // departure.dispatchEvent(new Event('change'));
        // arrival.dispatchEvent(new Event('change'));

        this.handleDeparturePortChange({ target: departure });

        arrival.value = departureValue;

        this.handleArrivalPortChange({ target: arrival });
    }

    /**
     * Handles the departure location change.
     *
     * @param {Event} event
     */
    handleDeparturePortChange(event) {
        this.DEBUG_EVENT && console.group('[App.BookingForm]', 'Handle Change Departure Location');
        this.DEBUG_EVENT && console.log('Event:', event);

        if (this.busy) {
            this.DEBUG_EVENT && console.log('Form is busy');
        } else {
            const arrivalID = event?.target?.id?.replace(FORM_CONTROL_DEPARTURE_LOCATION, FORM_CONTROL_ARRIVAL_LOCATION);
            if (arrivalID) {
                const arrivalTarget = document.getElementById(arrivalID);
                if (arrivalTarget) {
                    const arrivalValue = arrivalTarget.value;

                    purgeSelectOptions(arrivalTarget);

                    try {
                        fillArrivalPorts(event.target, arrivalTarget);
                    } catch (err) {
                        this.status.hidden = false;
                        this.status = this.getData('error-message');

                        console.error('[App.BookingForm]', 'Could not handle departure location change:', err);
                    }

                    arrivalTarget.value = arrivalValue;
                    this.updateArrivalPortPlaceholder(arrivalTarget);
                }
            }

            if (event?.target) {
                this.updateDeparturePortPlaceholder(event.target);
            }
        }

        this.DEBUG_EVENT && console.groupEnd();
    }

    /**
     * Handles the arrival location change.
     *
     * @param {Event} event
     *
     * @todo Implement additional passenger types.
     */
    handleArrivalPortChange(event) {
        this.DEBUG_EVENT && console.group('[App.BookingForm]', 'Handle Change Arrival Location');
        this.DEBUG_EVENT && console.log('Event:', event);

        if (this.busy) {
            this.DEBUG_EVENT && console.log('Form is busy');
        } else {
            if (event?.target) {
                this.updateArrivalPortPlaceholder(event.target);
            }
        }

        this.DEBUG_EVENT && console.groupEnd();
    }

    /**
     * Handles the form validation.
     */
    handleFormInvalid() {
        this.DEBUG_EVENT && console.group('[App.BookingForm]', 'Handle Invalid Form');
        this.el.classList.add(FORM_CLASS.WAS_VALIDATED);
        this.el.classList.add(FORM_CLASS.IS_INVALID);
        this.DEBUG_EVENT && console.groupEnd();
    }

    /**
     * Handles the form submission.
     *
     * @param {Event} event
     */
    handleFormSubmit(event) {
        event.preventDefault();

        this.DEBUG_EVENT && console.group('[App.BookingForm]', 'Handle Submit Form');
        this.DEBUG_EVENT && console.log('Event:', event);

        if (this.busy) {
            this.DEBUG_EVENT && console.log('Form is busy');
            this.DEBUG_EVENT && console.groupEnd();
            return;
        }

        this.busy = true;

        this.el.classList.remove(FORM_CLASS.HAS_ERROR);
        this.el.classList.remove(FORM_CLASS.IS_INVALID);
        this.el.classList.add(FORM_CLASS.IS_BUSY);
        this.el.classList.add(FORM_CLASS.WAS_VALIDATED);

        this.status.hidden = false;
        this.status = this.getData('status-processing');

        this.#handleFormSubmit(event.target)
            .then((reason) => {
                this.DEBUG_FORM_SUBMIT && console.log('Form Submission Fulfilled:', reason);

                if (typeof reason !== 'string' || !reason.length) {
                    reason = this.getData('status-available');
                }
                this.status.hidden = true;
                this.status = reason;
            })
            .catch((reason) => {
                this.DEBUG_FORM_SUBMIT && console.log('Form Submission Rejected:', reason);

                if (typeof reason !== 'string' || !reason.length) {
                    reason = this.getData('error-message');
                }
                this.status.hidden = false;
                this.status = reason;
                this.el.classList.add(FORM_CLASS.HAS_ERROR);

                console.error('[App.BookingForm]', 'Could not handle form submit:', reason);
            })
            .finally(() => {
                this.busy = false;
                this.el.classList.remove(FORM_CLASS.IS_BUSY);
            });

        this.DEBUG_EVENT && console.groupEnd();
    }

    /**
     * @param {HTMLSelectElement} departure
     */
    updateDeparturePortPlaceholder(departure) {
        this.DEBUG_EVENT && console.group('[App.BookingForm]', 'Update Departure Location Placeholder');

        const placeholderCity = document.querySelector(`[data-placeholder-departure-city="${departure.id}"]`);
        const placeholderCode = document.querySelector(`[data-placeholder-departure-code="${departure.id}"]`);

        if (!placeholderCity && !placeholderCode) {
            return;
        }

        const option = departure.selectedOptions[0] ?? departure.options[0];

        if (placeholderCity) {
            placeholderCity.textContent = option?.dataset?.portCity ?? 'N/A';
        }

        if (placeholderCode) {
            placeholderCode.textContent = option?.dataset?.portCode ?? 'N/A';
        }

        this.DEBUG_EVENT && console.groupEnd();
    }

    /**
     * @param {HTMLSelectElement} arrival
     */
    updateArrivalPortPlaceholder(arrival) {
        this.DEBUG_EVENT && console.group('[App.BookingForm]', 'Update Arrival Location Placeholder');

        const placeholderCity = document.querySelector(`[data-placeholder-arrival-city="${arrival.id}"]`);
        const placeholderCode = document.querySelector(`[data-placeholder-arrival-code="${arrival.id}"]`);

        if (!placeholderCity && !placeholderCode) {
            return;
        }

        const option = arrival.selectedOptions[0] ?? arrival.options[0];

        if (placeholderCity) {
            placeholderCity.textContent = option?.dataset?.portCity ?? 'N/A';
        }

        if (placeholderCode) {
            placeholderCode.textContent = option?.dataset?.portCode ?? 'N/A';
        }

        this.DEBUG_EVENT && console.groupEnd();
    }

    init() {
        this.busy = true;
        this.disabled = true;

        if (typeof this.DEBUG_API_INIT !== 'number') {
            this.DEBUG && console.log('[App.BookingForm]', 'Initializing…');
            this.#init();
        } else if (this.DEBUG_API_INIT < 0) {
            this.DEBUG && console.log('[App.BookingForm]', 'Initialization suspended');
        } else {
            this.DEBUG && console.log('[App.BookingForm]', 'Delayed initializing…', `(${this.DEBUG_API_INIT} ms)`);
            setTimeout(
                () => this.#init(),
                this.DEBUG_API_INIT
            );
        }
    }

    #failApi(reason) {
        this.disabled = true;
        this.status.hidden = false;
        this.status = this.getData('status-unavailable');

        console.warn('[App.BookingForm]', 'Unavailable JS API:', reason);
    }

    #finishApi() {
        this.busy = false;

        this.$('hide-when-complete').forEach((element) => element.setAttribute('hidden', true));
        this.$('show-when-complete').forEach((element) => element.removeAttribute('hidden'));
    }

    /**
     * @param  {HTMLFormElement} form
     * @return {Promise<string>} The resolved or rejected reason phrase.
     */
    async #handleFormSubmit(form) {
        this.DEBUG_FORM_SUBMIT && console.group('[App.BookingForm]', 'Submitting…');

        const formData = new FormData(form);
        this.DEBUG_FORM_SUBMIT && console.log('Form Data:', formData);

        const request = new window.AvailabilityRequest();
        request.lang = html.lang?.toUpperCase();
        request.currency = DEFAULT_CURRENCY;
        request.departureDate = [];
        request.depPort = [];
        request.arrPort = [];
        request.passengerQuantities = [];

        this.DEBUG_FORM_SUBMIT && console.group('Build Availability Request:');
        for (const [ key, value ] of formData) {
            this.DEBUG_FORM_SUBMIT && console.log('•', key, ':', value);
            switch (true) {
                case (key === FORM_CONTROL_CABIN_CLASS):
                case (key === FORM_CONTROL_COUPON_PROMO):
                case (key === FORM_CONTROL_COUPON_RAAP):
                case (key === FORM_CONTROL_CURRENCY):
                case (key === FORM_CONTROL_TRIP_TYPE): {
                    if (value != null && value !== '') {
                        request[key] = value;
                    }
                    break;
                }

                case (key.includes(`[${FORM_CONTROL_DEPARTURE_DATE}]`)): {
                    const formattedDate = formatDate(value);
                    if (formattedDate) {
                        request.departureDate.push(formattedDate);
                    }
                    break;
                }

                case (key.includes(`[${FORM_CONTROL_RETURN_DATE}]`)): {
                    const formattedDate = formatDate(value);
                    if (formattedDate) {
                        request.returnDate = formatDate(value);
                    }
                    break;
                }

                case (key.includes(`[${FORM_CONTROL_DEPARTURE_LOCATION}]`)): {
                    request.depPort.push(value);
                    break;
                }

                case (key.includes(`[${FORM_CONTROL_ARRIVAL_LOCATION}]`)): {
                    request.arrPort.push(value);
                    break;
                }

                case (key.includes(`[${FORM_CONTROL_SENIORS_PASSENGER_COUNT}]`)): {
                    const count = Number.parseInt(value);
                    if (count > 0) {
                        request.passengerQuantities.push(new window.PassengerQuantity('SENR', '', count));
                    }
                    break;
                }

                case (key.includes(`[${FORM_CONTROL_ADULTS_PASSENGER_COUNT}]`)): {
                    const count = Number.parseInt(value);
                    if (count > 0) {
                        request.passengerQuantities.push(new window.PassengerQuantity('ADULT', '', count));
                    }
                    break;
                }

                case (key.includes(`[${FORM_CONTROL_CHILDS_PASSENGER_COUNT}]`)): {
                    const count = Number.parseInt(value);
                    if (count > 0) {
                        request.passengerQuantities.push(new window.PassengerQuantity('CHILD', '', count));
                    }
                    break;
                }

                case (key.includes(`[${FORM_CONTROL_INFANTS_PASSENGER_COUNT}]`)): {
                    const count = Number.parseInt(value);
                    if (count > 0) {
                        request.passengerQuantities.push(new window.PassengerQuantity('INFANT', '', count));
                    }
                    break;
                }
            }
        }
        this.DEBUG_FORM_SUBMIT && console.groupEnd();

        this.DEBUG_FORM_SUBMIT && console.log('Availability Request (1/3):', { ...request });

        if (!request.tripType) {
            throw new Error(`'${FORM_CONTROL_TRIP_TYPE}' is missing`);
        }

        const baseSelector = form.hasAttribute('data-module-tablist')
            ? '.c-tab_body_item.is-active '
            : '';

        const isMultiTrip = (request.tripType === TRIP_TYPE_MULTITRIP);

        this.DEBUG_FORM_SUBMIT && console.log('Validate Availability Request…');

        const segmentCount = Math.max(1, request.departureDate.length, request.depPort.length, request.arrPort.length);
        if (segmentCount < 1 || segmentCount > apiCache.maxSegmentCount) {
            // throw new Error(`Expected between 1 and ${apiCache.maxSegmentCount} segments`);
            const fieldset = form.querySelector(baseSelector);
            const label    = ((fieldset && findFormFieldsetLabel(fieldset)) ?? `[${TRIP_TYPE_MULTITRIP}]`);
            const message  = (fieldset
                ? reportValidity(fieldset, {
                    rangeOverflow:  (segmentCount > apiCache.maxSegmentCount),
                    rangeUnderflow: (segmentCount < 1),
                })
                : null);

            this.DEBUG_FORM_SUBMIT && console.warn('Invalid Request:', 'segmentCount', i, label) && console.groupEnd();
            return Promise.reject(!fieldset?.willValidate && formatValidityMessage(message, (isMultiTrip ? `${label} #${i + 1}` : label)));
        }

        for (let i = 0; i < segmentCount; i++) {
            if (!request.depPort[i]) {
                // throw new Error(`'${FORM_CONTROL_DEPARTURE_LOCATION}' is missing`);
                const control = form.querySelector(`${baseSelector}[name="trips[${i}][${FORM_CONTROL_DEPARTURE_LOCATION}]"]`);
                const label   = ((control && findFormControlLabel(control)) ?? `[${FORM_CONTROL_DEPARTURE_LOCATION}]`);
                const message = (control
                    ? reportValidity(control, {
                        valueMissing: true,
                    })
                    : null);

                this.DEBUG_FORM_SUBMIT && console.warn('Invalid Request:', 'depPort', i, label) && console.groupEnd();
                return Promise.reject(!control?.willValidate && formatValidityMessage(message, (isMultiTrip ? `${label} #${i + 1}` : label)));
            }

            if (!request.arrPort[i]) {
                // throw new Error(`'${FORM_CONTROL_ARRIVAL_LOCATION}' is missing`);
                const control = form.querySelector(`${baseSelector}[name="trips[${i}][${FORM_CONTROL_ARRIVAL_LOCATION}]"]`);
                const label   = ((control && findFormControlLabel(control)) ?? `[${FORM_CONTROL_ARRIVAL_LOCATION}]`);
                const message = (control
                    ? reportValidity(control, {
                        valueMissing: true,
                    })
                    : null);

                this.DEBUG_FORM_SUBMIT && console.warn('Invalid Request:', 'arrPort', i, label) && console.groupEnd();
                return Promise.reject(!control?.willValidate && formatValidityMessage(message, (isMultiTrip ? `${label} #${i + 1}` : label)));
            }

            const control = form.querySelector(`${baseSelector}[name="trips[${i}][${FORM_CONTROL_DEPARTURE_DATE}]"]`);
            if (request.departureDate[i]) {
                control?.setAttribute('aria-invalid', 'false');
            } else {
                // throw new Error(`'${FORM_CONTROL_DEPARTURE_DATE}' is missing`);
                const label   = ((control && findFormControlLabel(control)) ?? `[${FORM_CONTROL_DEPARTURE_DATE}]`);
                const message = (control
                    ? reportValidity(control, {
                        valueMissing: true,
                    })
                    : null);

                if (control && control.readOnly) {
                    control.focus();
                }

                this.DEBUG_FORM_SUBMIT && console.warn('Invalid Request:', 'departureDate', i, label) && console.groupEnd();
                return Promise.reject(!control?.willValidate && formatValidityMessage(message, (isMultiTrip ? `${label} #${i + 1}` : label)));
            }
        }

        if (request.tripType === TRIP_TYPE_ROUNDTRIP) {
            const control = form.querySelector(`${baseSelector}[name="trips[0][${FORM_CONTROL_RETURN_DATE}]"]`);
            if (request.returnDate) {
                control?.setAttribute('aria-invalid', 'false');
            } else {
                // throw new Error(`'${FORM_CONTROL_RETURN_DATE}' is missing`);
                const label   = ((control && findFormControlLabel(control)) ?? `[${FORM_CONTROL_RETURN_DATE}]`);
                const message = (control
                    ? reportValidity(control, {
                        valueMissing: true,
                    })
                    : null);

                if (control && control.readOnly) {
                    control.focus();
                }

                this.DEBUG_FORM_SUBMIT && console.warn('Invalid Request:', 'returnDate', label) && console.groupEnd();
                return Promise.reject(!control?.willValidate && formatValidityMessage(message, label));
            }
        }

        if (request.passengerQuantities.length < 1 || request.passengerQuantities.length > apiCache.maxPassengerCount) {
            // throw new Error(`Expected between 1 and ${apiCache.maxPassengerCount} passengers`);
            const control = form.querySelector(`[name^="${FORM_CONTROL_PASSENGER_COUNT}"]`);
            const label   = ((control && findFormControlLabel(control)) ?? `[${FORM_CONTROL_PASSENGER_COUNT}]`);
            const message = (control
                ? reportValidity(control, {
                    rangeOverflow:  (request.passengerQuantities.length > apiCache.maxPassengerCount),
                    rangeUnderflow: (request.passengerQuantities.length < 1),
                })
                : null);

            this.DEBUG_FORM_SUBMIT && console.warn('Invalid Request:', 'passengerQuantities', label) && console.groupEnd();
            return Promise.reject(!control?.willValidate && formatValidityMessage(message, label));
        }

        request.departureDate = request.departureDate.join(',');
        request.depPort = request.depPort.join(',');
        request.arrPort = request.arrPort.join(',');

        this.DEBUG_FORM_SUBMIT && console.log('Availability Request (2/3):', { ...request });

        /**
         * Remove null and undefined values from request object
         * to shorten URL query.
         */
        for (const key in request) {
            if (request[key] == null) {
                delete request[key];
            }
        }

        this.DEBUG_FORM_SUBMIT && console.log('Availability Request (3/3):', { ...request });

        /**
         * Bypassing {@see CraneSearchAPI.searchV2} to avoid `window.location.replace()`
         * dropping the current URL from the browser's history.
         */
        const url = this.api.baseUrl + '/availability?' + jQuery.param(request);
        this.DEBUG_FORM_SUBMIT && console.log('URL:', url);

        if (this.DEBUG_FORM_TARGET) {
            window.open(url, this.DEBUG_FORM_TARGET, 'noopener');
        } else {
            window.location.assign(url);
        }

        this.DEBUG_FORM_SUBMIT && console.groupEnd();
    }

    #init() {
        whenApiIsReady(this.el.action)
            .then(() => this.#initApi())
            .catch((reason) => this.#failApi(reason))
            .finally(() => this.#finishApi());
    }

    async #initApi() {
        this.api ??= new window.CraneSearchAPI(this.el.action);
        this.DEBUG_API_INIT && console.log('[App.BookingForm]', 'API:', this.api);

        await this.#initPortOptions();
        // await this.#initCurrencies();
        // await this.#initCabinClasses();
        await this.#initPassengerCounts();
        await this.#initSegmentCounts();

        this.$('enable-when-available').forEach((element) => {
            if (element.hasAttribute('disabled')) {
                element.removeAttribute('disabled');
            }

            if (element.hasAttribute('tabindex') && element.tabIndex === -1) {
                element.removeAttribute('tabindex');
            }
        });

        this.disabled = false;
        this.status.hidden = true;
        this.status = this.getData('status-available');

        this.DEBUG && console.log('[App.BookingForm]', 'Form is ready');
    }

    /**
     * Loads ports from API and fills related departure port form controls.
     */
    async #initPortOptions() {
        apiCache.portGroups ??= await this.api.portGroups();
        this.DEBUG_API_INIT && console.log('[CraneSearchAPI.portGroups]', 'Port Groups:', apiCache.portGroups);

        /**
         * Fill arrival ports by each departure port.
         *
         * @param {Port} port
         */
        const fillPortMatrix = (port) => {
            /**
             * @param {PortGroups[]}
             */
            this.api.portGroupsFor(port.code).then((groups) => {
                this.DEBUG_API_INIT && console.log('[CraneSearchAPI.portGroupsFor]', 'Port Groups For:', port.code, groups);
                if (groups[0]) {
                    apiCache.portGroupsByPort[port.code] = groups[0];
                }
            });
        };

        const selects = this.$('departure-port');
        if (selects.length) {
            const options = createSelectOptions(
                apiCache.portGroups,
                /**
                 * @param  {HTMLOptionElement} option
                 * @param  {Port}              port
                 * @return {HTMLOptionElement}
                 */
                (option, port) => {
                    fillPortMatrix(port);

                    return fillSelectOptionWithPort(option, port);
                }
            );

            if (options.childElementCount) {
                selects.forEach((select) => {
                    if (select?.options.length > 1) {
                        return;
                    }

                    select.append(options.cloneNode(true));
                });
            }
        } else {
            iterateCollections(apiCache.portGroups, fillPortMatrix);
        }

        this.DEBUG_API_INIT && console.log('Port Groups by Port:', apiCache.portGroupsByPort);
    }

    /**
     * Loads currencies from API and fills related form controls.
     *
     * @todo Enable this feature if needed.
     */
    async #initCurrencies() {
        const selects = this.$('currency');
        if (!selects.length) {
            return;
        }

        apiCache.currencies ??= await this.api.currencies().then(
            /**
             * @param {Currencies}
             */
            (currencies) => {
                const set = new Set(
                    currencies?.defaultCurrency?.code
                    ? [ currencies.defaultCurrency.code ]
                    : []
                );
                currencies?.values?.forEach((currency) => set.add(currency.code));
                return Array.from(set);
            }
        );
        this.DEBUG_API_INIT && console.log('[CraneSearchAPI.currencies]', 'Currencies:', apiCache.currencies);

        const options = createSelectOptions(apiCache.currencies);
        if (options.childElementCount) {
            selects.forEach((select) => {
                if (select?.options.length > 1) {
                    return;
                }

                select.append(options.cloneNode(true));
            });
        }
    }

    /**
     * Loads cabin service classes from API and fills related form controls.
     *
     * @todo Enable this feature if needed.
     */
    async #initCabinClasses() {
        const selects = this.$('cabin-class');
        if (!selects.length) {
            return;
        }

        apiCache.cabinClasses ??= await this.api.cabinClasses();
        this.DEBUG_API_INIT && console.log('[CraneSearchAPI.cabinClasses]', 'Cabin Classes:', apiCache.cabinClasses);

        const options = createSelectOptions(apiCache.cabinClasses);
        if (options.childElementCount) {
            selects.forEach((select) => {
                if (select?.options.length > 1) {
                    return;
                }

                select.append(options.cloneNode(true));
            });
        }
    }

    /**
     * Loads maximum passenger counts from API and fills related form controls.
     */
    async #initPassengerCounts() {
        const controls = this.$('passengers');
        if (!controls.length) {
            return;
        }

        apiCache.maxPassengerCount ??= ((await this.api.maxPassengerCount()) || DEFAULT_MAX_PASSENGER_COUNT);
        this.DEBUG_API_INIT && console.log('[CraneSearchAPI.maxPassengerCount]', 'Maximum Passenger Count:', apiCache.maxPassengerCount);

        let options;
        controls.forEach((control) => {
            if (control instanceof HTMLSelectElement) {
                if (control?.options.length > 1) {
                    options ??= createSelectOptions(Array.from(Array(apiCache.maxPassengerCount).keys()));
                    if (options.childElementCount) {
                        control.append(options.cloneNode(true));
                    }
                }
            } else if (control instanceof HTMLInputElement) {
                if (control?.type.toUpperCase() === 'NUMBER') {
                    control.setAttribute('max', apiCache.maxPassengerCount);
                }
            }
        });
    }

    /**
     * Loads maximum segment counts (multi-trip) from API and fills
     * related form controls.
     */
    async #initSegmentCounts() {
        const containers = this.$('segments');
        if (!containers.length) {
            return;
        }

        apiCache.maxSegmentCount ??= ((await this.api.maxSegmentCount()) || DEFAULT_MAX_SEGMENT_COUNT);
        this.DEBUG_API_INIT && console.log('[CraneSearchAPI.maxSegmentCount]', 'Maximum Segment Count:', apiCache.maxSegmentCount);

        containers.forEach((container) => {
            if (!container.hasAttribute('data-module-duplicate-field')) {
                return;
            }

            container.dataset.listMax = apiCache.maxSegmentCount;
        });
    }
}

/**
 * Creates a collection of `<optgroup>` and `<option>` elements.
 *
 * @param  {Array|{[string]: Array}} collections - One or more collections of options.
 * @param  {?OptionMapper}           [iteratee]  - The function to transform options.
 * @param  {?Node}                   [parent]    - The element to add the options to.
 * @throws {TypeError} If the collections parameter is invalid.
 * @return {DocumentFragment}
 */
function createSelectOptions(collections, iteratee = null, parent = null) {
    if (collections == null) {
        throw new TypeError(`createSelectOptions expected an iterable collection, received nil`);
    }

    iteratee ??= (option, item) => {
        option.value     = (item?.value ?? item);
        option.innerText = (item?.label ?? item);

        return option;
    };

    if (Array.isArray(collections)) {
        collections = { '': collections };
    }

    const hasManyGroups = (Object.keys(collections).length > 1);

    const fragment = document.createDocumentFragment();

    for (const [ label, items ] of Object.entries(collections)) {
        const options = hasManyGroups
            ? createSelectOptionGroup(label, fragment)
            : fragment;

        items.forEach((item, index) => {
            const option = iteratee(
                document.createElement('option'),
                item,
                index,
                items
            );

            if (options) {
                options.append(option);
            }
        });
    }

    if (parent) {
        parent.append(fragment);
    }

    return fragment;
}

/**
 * Creates a `<optgroup>` element.
 *
 * @param  {string} label    - The name if the group of options.
 * @param  {?Node}  [parent] - The element to add the group to.
 * @return {HTMLOptGroupElement}
 */
function createSelectOptionGroup(label, parent = null) {
    const group = document.createElement('optgroup');
    group.label = label;

    if (parent) {
        parent.append(group);
    }

    return group;
}

/**
 * @param  {HTMLSelectElement} departure
 * @param  {HTMLSelectElement} arrival
 * @throws {Error} If the departure's selected value is not in the {@see portMatrix}.
 */
function fillArrivalPorts(departure, arrival) {
    if (!departure?.value) {
        return;
    }

    if (!apiCache.portGroupsByPort[departure.value]) {
        throw new Error(`Expected port LUT to contain "${departure.value}"`);
    }

    createSelectOptions(
        apiCache.portGroupsByPort[departure.value],
        fillSelectOptionWithPort,
        arrival
    );
}

/**
 * @param  {HTMLOptionElement} option
 * @param  {Port}              port
 * @return {HTMLOptionElement}
 */
function fillSelectOptionWithPort(option, port) {
    option.value     = port.code;
    option.innerText = `${port.cityName} (${port.code})`;

    option.dataset.portCity = port.cityName;
    option.dataset.portCode = port.code;

    return option;
}

/**
 * @param  {HTMLElement} control
 * @return {?string}
 */
function findFormControlLabel(control) {
    const label = control.labels?.[0];
    if (!label) {
        return null;
    }

    // Strip any required asterisk.
    return label.innerText.replace(/\s*\*\s*/, '').trim() || null;
}

/**
 * @param  {HTMLElement} fieldset
 * @return {?string}
 */
function findFormFieldsetLabel(fieldset) {
    const legend = fieldset.querySelector('legend');
    if (!legend) {
        return null;
    }

    // Strip any required asterisk.
    return legend.innerText.replace(/\s*\*\s*/, '').trim() || null;
}

/**
 * Transforms ISO-8601 date format (`YYYY-MM-DD`) to Tukey's notation (`DD.MM.YYYY`).
 *
 * @param  {string} date
 * @return {string}
 */
function formatDate(date) {
    return date.replace(/(\d{4})-(\d{2})-(\d{2})/, '$3.$2.$1');
}

/**
 * @param  {string}  message
 * @param  {?string} label
 * @return {string}
 */
function formatValidityMessage(message, label = null) {
    if (label && message) {
        return `${label}: ${message}`;
    }

    return message;
}

/**
 * Applies a given function to each value of each collection.
 *
 * @param  {Array|{[string]: Array}} collections - One or more collections.
 * @param  {?Function}               iteratee    - The function to apply to each value of the collections.
 */
function iterateCollections(collections, iteratee) {
    if (Array.isArray(collections)) {
        collections = { '': collections };
    }

    for (const [ , values ] of Object.entries(collections)) {
        values.forEach(iteratee);
    }
}

/**
 * Removes all but the placeholder `<option>` element
 * (whose value is an empty string).
 *
 * @param {HTMLSelectElement} select
 */
function purgeSelectOptions(select) {
    if (select?.children) {
        Array.from(select.children).forEach((child) => {
            if (child.value === '') {
                child.selected = true;
            } else {
                child.remove();
            }
        });
    }
}
